/**
 * Utilities for sanitizing numeric input coming from free-form text fields.
 */

/**
 * Removes all non-digit characters from the provided text.
 */
export const sanitizeIntegerInput = (text: string): string => {
  if (!text) return '';
  return text.replace(/[^0-9]/g, '');
};

/**
 * Sanitizes decimal input by allowing digits and a single decimal separator.
 * - Collapses multiple dots into a single decimal point
 * - Prefixes values that start with "." with a leading 0 (".5" -> "0.5")
 * - Preserves trailing decimal points so the user can continue typing (e.g., "1." becomes "1.")
 */
export const sanitizeDecimalInput = (text: string): string => {
  if (!text) return '';

  let sanitized = text.replace(/[^0-9.]/g, '');

  const firstDotIndex = sanitized.indexOf('.');
  if (firstDotIndex !== -1) {
    const beforeDot = sanitized.slice(0, firstDotIndex + 1);
    const afterDot = sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
    sanitized = beforeDot + afterDot;
  }

  if (sanitized.startsWith('.')) {
    sanitized = `0${sanitized}`;
  }

  return sanitized;
};

/**
 * Returns true when the sanitized decimal string contains digits on both sides
 * of the decimal separator (or no decimal separator at all).
 */
export const isCompleteDecimalValue = (text: string): boolean => {
  if (!text) return false;
  return /^\d+(\.\d+)?$/.test(text);
};

/**
 * Converts a sanitized decimal string to a numeric value.
 * Trailing decimal separators are ignored ("1." -> 1).
 */
export const parseSanitizedDecimal = (text: string): number | undefined => {
  if (!text) return undefined;
  const normalized = text.endsWith('.') ? text.slice(0, -1) : text;
  if (!normalized) return undefined;
  return parseFloat(normalized);
};
